import { useApi } from "@/hooks";
import { AppContext } from "@Context/common/general/context";
import { trainingPlansEvents } from "@Context/game/training-plans/training-plans-events";
import { useTrainingPlansProgressContext } from "@Context/game/training-plans/training-plans-progress-context";
import PlanTypes from "@Enums/PlanTypes";
import { Api } from "@Shared/schemas";
import { isToday } from "date-fns";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Header-level entry hook for Training Plans.
 *
 * Responsibility:
 * - Decides whether the Training Plans header button should be shown.
 * - Exposes the header action that opens the drawer via event bus.
 */
export type TrainingPlanHeaderStatus = "loading" | "not-started" | "active" | "complete" | "unavailable";

export const useTrainingPlans = () => {
  const { state } = useContext(AppContext);
  const { isProgressResolved, progressData, progressError } = useTrainingPlansProgressContext();
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasAvailablePlans, setHasAvailablePlans] = useState(false);

  const { call: getAvailableTrainingPlans } = useApi<typeof Api.TrainingPlans.GetPlans>({
    api: Api.TrainingPlans.GetPlans,
    request: {
      plan_type: PlanTypes.TRAINING,
    },
    disableLoader: true,
  });

  const isAuthenticated = state.user.authenticated;
  const hasSelectedAccount = Boolean(state.account.id);
  const hasSubscription = state.user.hasAccessSubscriptions && !state.user.hasAllAccessExpired;
  const hasCompletedBootcamp = !state.user.inBootcamp;
  const canUseTrainingPlans = isAuthenticated && hasSelectedAccount && hasSubscription && hasCompletedBootcamp;

  const refreshAvailability = useCallback(async () => {
    if (!canUseTrainingPlans) {
      setHasAvailablePlans(false);
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);

    try {
      const plansResult = (await getAvailableTrainingPlans()) ?? [];
      setHasAvailablePlans(plansResult.length > 0);
    } finally {
      setIsInitializing(false);
    }
  }, [canUseTrainingPlans, getAvailableTrainingPlans]);

  useEffect(() => {
    void refreshAvailability();
  }, [refreshAvailability]);

  const openFromHeader = useCallback(async () => {
    if (!canUseTrainingPlans || isInitializing) {
      return;
    }
    trainingPlansEvents.emitOpenDrawer();
  }, [canUseTrainingPlans, isInitializing]);

  const headerStatus = useMemo<TrainingPlanHeaderStatus>(() => {
    if (isInitializing || (!isProgressResolved && !progressData)) {
      return "loading";
    }

    if (progressError) {
      return "unavailable";
    }

    if (!progressData?.enrollment?.is_active) {
      return "not-started";
    }

    const currentPeriod = progressData.current_period;

    if (!currentPeriod || currentPeriod.required_sessions_count <= 0) {
      return "unavailable";
    }

    return currentPeriod.is_completed || currentPeriod.completed_sessions_count >= currentPeriod.required_sessions_count ? "complete" : "active";
  }, [isInitializing, isProgressResolved, progressData, progressError]);

  const hasCompletedTrainingToday = useMemo(
    () => progressData?.qualifying_sessions.some(session => isToday(new Date(session.counted_at))) ?? false,
    [progressData?.qualifying_sessions]
  );

  return useMemo(
    () => ({
      shouldShowTrainingReminder: headerStatus === "active" && !hasCompletedTrainingToday,
      shouldShowHeaderButton: canUseTrainingPlans && (isInitializing || hasAvailablePlans),
      headerStatus,
      openFromHeader,
    }),
    [canUseTrainingPlans, hasAvailablePlans, hasCompletedTrainingToday, headerStatus, isInitializing, openFromHeader]
  );
};
